import { forwardRef, useEffect, useImperativeHandle, type ReactNode } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Bold, Italic, Link2, List, ListOrdered, Underline as UnderlineIcon } from 'lucide-react'
import { cn } from '@/lib/cn'
import { richTextClass } from '@/components/RichText'
import { TokenNode, tokensToChips } from '@/components/richTextToken'

/** Imperative surface so a parent can splice a personalization {{token}} at the caret. */
export interface RichTextEditorHandle {
  insertText: (text: string) => void
}

/**
 * Reusable WYSIWYG editor (tiptap) for rich-text notes and email bodies. Controlled: {@code value}
 * is HTML in, {@code onChange} emits HTML out (empty editor emits '' so callers can guard on
 * `body.trim()`). The toolbar covers bold / italic / underline / bullet & numbered lists / link;
 * the stored HTML is sanitized on the backend before it is persisted or sent. A {{token}} shows as a
 * chip in words (TokenNode) but is stored as the same {{token}} text.
 */
const RichTextEditor = forwardRef<
  RichTextEditorHandle,
  { value: string; onChange: (html: string) => void; placeholder?: string; className?: string }
>(function RichTextEditor({ value, onChange, placeholder, className }, ref) {
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // noopener/noreferrer guard against reverse tabnabbing from _blank links
        link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' } },
      }),
      TokenNode,
    ],
    content: tokensToChips(value || ''),
    onUpdate: ({ editor }) => onChange(editor.isEmpty ? '' : editor.getHTML()),
    editorProps: {
      // Named like a textarea for screen readers (and tests): the placeholder says what goes here.
      attributes: {
        class: cn('outline-none min-h-[6rem] px-3 py-2', richTextClass),
        role: 'textbox',
        'aria-multiline': 'true',
        ...(placeholder ? { 'aria-label': placeholder } : {}),
      },
    },
  })

  // Sync when the value is driven from outside (reset to '' after submit, or seeded for an edit).
  // Compares against the current HTML so typing (which flows back through onChange) never loops.
  useEffect(() => {
    if (!editor) return
    const incoming = tokensToChips(value || '')
    if (incoming === (editor.isEmpty ? '' : editor.getHTML())) return
    editor.commands.setContent(incoming, { emitUpdate: false })
  }, [value, editor])

  // The placeholder follows the controlled `value`, not editor.isEmpty: seeding content with
  // setContent({ emitUpdate: false }) deliberately skips the update event, so editor.isEmpty stays
  // stale and the placeholder used to sit on top of freshly loaded text (e.g. a message template).
  const isEmpty = !value || value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === ''

  useImperativeHandle(
    ref,
    () => ({
      insertText: (text: string) => {
        // A lone {{token}} goes in as a chip; anything else as plain text.
        const token = /^\{\{([a-z0-9_.]+)\}\}$/.exec(text.trim())
        editor?.chain().focus().insertContent(token ? { type: 'token', attrs: { name: token[1] } } : text).run()
      },
    }),
    [editor],
  )

  function toggleLink() {
    if (!editor) return
    const prev = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Link URL', prev ?? 'https://')
    if (url === null) return
    const chain = editor.chain().focus().extendMarkRange('link')
    if (url.trim() === '') chain.unsetLink().run()
    else chain.setLink({ href: url.trim() }).run()
  }

  return (
    <div
      className={cn(
        'rounded-lg border border-gray-200 focus-within:ring-2 focus-within:ring-brand-primary/40',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-100 px-1.5 py-1">
        <ToolbarButton active={editor?.isActive('bold')} onClick={() => editor?.chain().focus().toggleBold().run()} title="Bold">
          <Bold size={14} />
        </ToolbarButton>
        <ToolbarButton active={editor?.isActive('italic')} onClick={() => editor?.chain().focus().toggleItalic().run()} title="Italic">
          <Italic size={14} />
        </ToolbarButton>
        <ToolbarButton active={editor?.isActive('underline')} onClick={() => editor?.chain().focus().toggleUnderline().run()} title="Underline">
          <UnderlineIcon size={14} />
        </ToolbarButton>
        <ToolbarButton active={editor?.isActive('bulletList')} onClick={() => editor?.chain().focus().toggleBulletList().run()} title="Bullet list">
          <List size={14} />
        </ToolbarButton>
        <ToolbarButton active={editor?.isActive('orderedList')} onClick={() => editor?.chain().focus().toggleOrderedList().run()} title="Numbered list">
          <ListOrdered size={14} />
        </ToolbarButton>
        <ToolbarButton active={editor?.isActive('link')} onClick={toggleLink} title="Link">
          <Link2 size={14} />
        </ToolbarButton>
      </div>
      <div className="relative">
        <EditorContent editor={editor} />
        {placeholder && isEmpty && (
          <p className="pointer-events-none absolute left-3 top-2 select-none text-sm text-gray-400">{placeholder}</p>
        )}
      </div>
    </div>
  )
})

function ToolbarButton({
  active,
  onClick,
  title,
  children,
}: {
  active?: boolean
  onClick: () => void
  title: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={cn(
        'rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-brand-dark',
        active && 'bg-brand-primary/10 text-brand-primary',
      )}
    >
      {children}
    </button>
  )
}

export default RichTextEditor
